import axios from 'axios';

/**
 * Global 401 handling: an expired or revoked session ends at the login page
 * instead of a half-rendered app.
 *
 * PrivateRoute (App.js) only checks that a token EXISTS. A token that is
 * present but expired passes that check, so the user lands on a real page and
 * every request behind it fails — state.admin.data stays null and anything
 * reading it (Users.jsx's organization scoping, Nav's role menu) silently
 * misbehaves. Catching 401 centrally turns that into a clean re-login.
 *
 * Imported for its side effects from index.js, before <App /> renders.
 */

// Pages the app is allowed to sit on with no session at all.
const PUBLIC_PATHS = ['/login', '/logged-out', '/invite', '/reset-password'];

/**
 * A 401 from the auth flow is an ANSWER, not an expired session:
 * /api/auth/login returns 401 for a wrong password, /api/auth/mfa/verify for a
 * wrong code. Redirecting on those would blow away the form — and its error
 * message — while the user is still trying to sign in.
 */
const isAuthEndpoint = (url = '') => String(url).includes('/auth/');

const onPublicPath = () => PUBLIC_PATHS.includes(window.location.pathname);

// Several requests can fail together (App.js fires user + org + task fetches on
// mount). Without this latch each one would trigger its own navigation.
let redirecting = false;

export function handleUnauthorized(url) {
  if (redirecting || isAuthEndpoint(url) || onPublicPath()) return;
  redirecting = true;
  localStorage.clear();
  window.location.href = '/login';
}

// Note: only 401 (unauthenticated) logs out. A 403 means the session is valid
// but the action isn't permitted — the caller should surface that, not sign the
// user out.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      handleUnauthorized(error.config?.url || '');
    }
    return Promise.reject(error);
  },
);

// Axios interceptors don't see bare fetch(), and 44 call sites across 18 files
// still use it (Api/req.js, slice/apiSlice.js, Sidebar.jsx, SharedView.jsx,
// ChatBlob.jsx, ...). Covering them one by one would mean an apiFetch helper
// threaded through all 18 — and would only hold for as long as everyone
// remembered to use it. Wrapping window.fetch covers them all, including code
// written later. It reads .status and returns the response untouched, so
// callers behave exactly as before.
//
// The flag makes this idempotent: without it, a hot-reload re-evaluates this
// module and nativeFetch captures the ALREADY-wrapped fetch, chaining a new
// layer on every reload.
if (!window.__authFetchWrapped) {
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const response = await nativeFetch(input, init);
    // Belt and braces: this wrapper sits in front of EVERY request in the app,
    // so it must not be able to break one. It never reads the body (that would
    // consume the stream the caller needs), and any error in the check itself
    // is swallowed — a failure to redirect is an annoyance, a failure to return
    // the response is an outage.
    try {
      if (response.status === 401) {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        handleUnauthorized(url);
      }
    } catch (e) {
      console.error('authInterceptor: 401 check failed', e);
    }
    return response;
  };
  window.__authFetchWrapped = true;
}
